import React from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import MapHeaderIcon from './heder/MapHeaderIcon'
import PdfIcon from '@assets/map_header_icons/Pdf.svg'
import ExcelIcon from '@assets/map_header_icons/Excel.svg'
import CopyIcon from '@assets/map_header_icons/Copy.svg'
import PrintIcon from '@assets/map_header_icons/Print.svg'
import BackArrowIcon from '@assets/ConsumerHeader/Vector 38.svg'
import { exportToExcel, exportToPDF, copyToClipboard } from '@/utils/exportUtils'
import { useTableStore } from '@/store/table/table'

type ConsumerHeaderProps = {
  title: string
  titlePrefix?: string // Added titlePrefix
  showSearch?: boolean
  placment?: 'top' | 'bottom'
  subTitle?: string
  onExcelExport?: () => void
  onPdfExport?: () => void
  onCopy?: () => void
  backTo?: string
  /** Sits between the title and the action icons. */
  center?: React.ReactNode
  /** 'meterPage' draws the 50px meter number with the address small beside it. */
  variant?: 'default' | 'meterPage'
}

const ConsumerHeader: React.FC<ConsumerHeaderProps> = ({
  title,
  titlePrefix = '', // Default to empty
  subTitle = '',
  onExcelExport,
  onPdfExport,
  onCopy,
  backTo,
  center,
  variant = 'default',
}) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isDarkMode = theme.palette.mode === 'dark'
  const { exportRowsStore, exportColumnsStore } = useTableStore()
  const meterPage = variant === 'meterPage'

  const iconConfigs = [
    {
      icon: ExcelIcon,
      alt: 'Excel',
      tooltip: 'ייצוא ל-Excel',
      onClick: () => {
        if (onExcelExport) onExcelExport()
        else exportToExcel(exportColumnsStore, exportRowsStore)
      },
    },
    {
      icon: PdfIcon,
      alt: 'PDF',
      tooltip: 'ייצוא ל-PDF',
      onClick: () => {
        if (onPdfExport) onPdfExport()
        else exportToPDF(exportColumnsStore, exportRowsStore)
      },
    },
    {
      icon: CopyIcon,
      alt: 'Copy',
      tooltip: 'העתקה',
      onClick: () => {
        if (onCopy) onCopy()
        else copyToClipboard(exportColumnsStore, exportRowsStore)
      },
    },
    {
      icon: PrintIcon,
      alt: 'Print',
      tooltip: 'הדפסה',
      onClick: () => window.print(),
    },
  ]

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        direction: 'rtl',
        mb: 2,
        px: 0.5,
        ...(meterPage && { flexWrap: 'wrap', rowGap: 1 }),
      }}
    >
      {/* Right Side: Title Group */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: meterPage ? 'baseline' : 'center',
              flexWrap: meterPage ? 'wrap' : undefined,
              gap: 2,
            }}
          >
            <img
              src={BackArrowIcon}
              alt="back"
              onClick={() => {
                if (backTo) {
                  navigate(backTo)
                  return
                }
                navigate(-1)
              }}
              style={{
                width: '10px',
                height: '18px',
                cursor: 'pointer',
                alignSelf: 'center',
                filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
              }}
            />
            <Typography
              fontWeight={meterPage ? 800 : 700}
              fontSize={meterPage ? '50px' : '40px'}
              color={isDarkMode ? '#BBD7FF' : '#003F61'}
              sx={{ fontFamily: 'Rubik, sans-serif' }}
            >
              {titlePrefix}
              {title}
            </Typography>
            {meterPage && subTitle && (
              <Typography
                sx={{
                  color: isDarkMode ? '#94A3B8' : '#9AA4B2',
                  fontFamily: 'Rubik',
                  fontSize: '20px',
                  fontWeight: 400,
                }}
              >
                {subTitle}
              </Typography>
            )}
          </Box>

          {!meterPage && subTitle && (
            <Typography
              sx={{
                color: isDarkMode ? '#94A3B8' : '#64748B',
                fontFamily: 'Rubik',
                fontSize: '15px',
                fontWeight: 400,
                mt: -0.5,
              }}
            >
              {subTitle}
            </Typography>
          )}
        </Box>
      </Box>

      {center ? (
        <Box
          className="no-print"
          sx={{ display: 'flex', justifyContent: 'center' }}
        >
          {center}
        </Box>
      ) : null}

      {/* Left Side: Action Icons */}
      <Box
        className="no-print"
        sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
      >
        {iconConfigs.map((config, index) => (
          <MapHeaderIcon
            key={index}
            icon={config.icon}
            alt={config.alt}
            onClick={config.onClick}
          />
        ))}
      </Box>
    </Box>
  )
}

export default ConsumerHeader
